import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CALENDAR_EVENT_KINDS,
  DEPLOYMENT_KIND_LABELS,
  EVENT_PREP_ITEMS,
  naverMapSearchUrl,
  type DeploymentKind,
} from '@bhops/shared';
import { TimeSelect } from '@/components/TimeSelect';
import { modelsApi } from '@/api/models';

// 이벤트 일정의 입력 칸(2026-11-02 오너 — "입력할 내용은 설치 날짜 시간, 회수 날짜 시간, 설치 장소, 이벤트
// 내용, 준비물 체크 리스트(캘린더 메모로 기록됨), 출고할 로봇"). 설치 날짜 · 시간은 부르는 쪽이 그린다
// (일정 추가 창은 맨 위 날짜 줄이 곧 설치 날짜). 일정 추가 창과 이벤트 고치기 창이 같이 쓴다.
// 같은 날 — 이벤트 유형(로봇 배치의 '유형'과 같은 말, 출고 기록의 유형이 된다) · 비고 · 지도 링크
// ("장소도 검색해서 네이버 지도 주소 링크 붙히게").

export interface EventFieldsValue {
  returnDate: string;
  returnTime: string;
  kind: DeploymentKind;
  location: string;
  mapUrl: string;
  content: string;
  note: string;
  checklist: string[];
  /** 모델 id → 대수 */
  lines: Record<string, number>;
}

export const emptyEventFields = (returnDate: string): EventFieldsValue => ({
  returnDate,
  returnTime: '',
  kind: 'EVENT',
  location: '',
  mapUrl: '',
  content: '',
  note: '',
  checklist: [],
  lines: {},
});

/** 저장할 수 있는가 — 회수일이 설치일보다 앞서면 까닭. */
export function eventFieldsProblem(installDate: string, v: EventFieldsValue): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v.returnDate)) return '회수 날짜를 골라 주세요.';
  if (v.returnDate < installDate) return '회수 날짜가 설치 날짜보다 앞섭니다.';
  return null;
}

/** 지도 링크는 비우거나 http(s) 주소. */
const mapUrlProblem = (v: EventFieldsValue) =>
  v.mapUrl.trim() && !/^https?:\/\/\S+$/i.test(v.mapUrl.trim())
    ? '지도 링크는 https:// 로 시작하는 주소여야 합니다.'
    : null;

export const eventFieldsReady = (installDate: string, v: EventFieldsValue) =>
  !eventFieldsProblem(installDate, v) &&
  !mapUrlProblem(v) &&
  !!v.location.trim() &&
  !!v.content.trim();

/** 저장할 때 보내는 유형 · 비고 · 지도 링크 — 빈 글은 null. */
export const eventExtras = (v: EventFieldsValue) => ({
  kind: v.kind,
  note: v.note.trim() || null,
  mapUrl: v.mapUrl.trim() || null,
});

export const eventRobotLines = (v: EventFieldsValue) =>
  Object.entries(v.lines)
    .filter(([, n]) => n > 0)
    .map(([modelId, quantity]) => ({ modelId, quantity }));

const MAX_PER_MODEL = 30;

// 이벤트에 나가는 모델은 GEN2 · 헤드리스 · Zephyr 뿐이다(2026-11-02 오너 — "나머진 나갈일 절대 없어").
const EVENT_MODEL_CODES: readonly string[] = ['GEN2', 'HEADLESS', 'ZEPHYR'];

export function EventScheduleFields({
  installDate,
  value,
  onChange,
}: {
  installDate: string;
  value: EventFieldsValue;
  onChange: (next: EventFieldsValue) => void;
}) {
  const modelsQ = useQuery({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 10 * 60_000,
  });
  const set = (patch: Partial<EventFieldsValue>) => onChange({ ...value, ...patch });
  const setQty = (modelId: string, n: number) =>
    set({ lines: { ...value.lines, [modelId]: Math.max(0, Math.min(MAX_PER_MODEL, n)) } });
  const total = Object.values(value.lines).reduce((s, n) => s + n, 0);
  const allPicked = value.checklist.length === EVENT_PREP_ITEMS.length;
  const problem = eventFieldsProblem(installDate, value);
  const mapProblem = mapUrlProblem(value);
  const place = value.location.trim();

  return (
    <>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ ...field, flex: '1 1 150px' }}>
          <span style={labelSt}>회수 날짜</span>
          <input
            type="date"
            value={value.returnDate}
            min={installDate}
            onChange={(e) => set({ returnDate: e.target.value })}
            style={input}
            data-testid="event-return-date"
          />
        </label>
        <label style={{ ...field, flex: '2 1 260px' }}>
          <span style={labelSt}>
            회수 시간 <span style={muted}>(선택)</span>
          </span>
          <TimeSelect
            value={value.returnTime}
            onChange={(t) => set({ returnTime: t })}
            style={input}
            ariaLabel="회수 시간 (선택)"
            testId="event-return-time"
          />
        </label>
      </div>
      {problem && value.returnDate && (
        <span style={warn} data-testid="event-date-problem">
          {problem}
        </span>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ ...field, flex: '1 1 150px' }}>
          <span style={labelSt}>이벤트 유형</span>
          <select
            value={value.kind}
            onChange={(e) => set({ kind: e.target.value as DeploymentKind })}
            style={input}
            data-testid="event-kind"
          >
            {CALENDAR_EVENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {DEPLOYMENT_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label style={{ ...field, flex: '2 1 260px' }}>
          <span style={labelSt}>설치 장소</span>
          <input
            value={value.location}
            onChange={(e) => set({ location: e.target.value })}
            maxLength={200}
            placeholder="예) 코엑스 B홀 3번 부스"
            style={input}
            data-testid="event-location"
          />
        </label>
      </div>

      <div style={field}>
        <label htmlFor="event-map-url" style={labelSt}>
          지도 링크 <span style={muted}>(선택)</span>
        </label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            id="event-map-url"
            type="url"
            inputMode="url"
            value={value.mapUrl}
            onChange={(e) => set({ mapUrl: e.target.value })}
            maxLength={500}
            placeholder="네이버 지도의 공유 링크를 붙여 넣으세요"
            style={{ ...input, flex: 1, minWidth: 0 }}
            data-testid="event-map-url"
          />
          <a
            href={place ? naverMapSearchUrl(place) : undefined}
            target="_blank"
            rel="noreferrer"
            aria-disabled={!place}
            onClick={(e) => {
              if (!place) e.preventDefault();
            }}
            style={{ ...mapBtn, ...(place ? null : mapBtnOff) }}
            className="kuun-press"
            data-testid="event-map-search"
          >
            <span aria-hidden style={naverMark}>
              N
            </span>
            네이버 지도에서 찾기
          </a>
        </div>
        <span style={mapProblem ? warnInline : hint}>
          {mapProblem ??
            (place
              ? '공유 링크를 붙이면 구글 캘린더 메모에 그 링크가, 비워 두면 장소 검색 링크가 들어갑니다.'
              : '설치 장소를 먼저 적으면 네이버 지도에서 찾아볼 수 있습니다.')}
        </span>
      </div>

      <label style={field}>
        <span style={labelSt}>이벤트 내용</span>
        <textarea
          value={value.content}
          onChange={(e) => set({ content: e.target.value })}
          rows={3}
          maxLength={2000}
          placeholder="예) 서울 푸드 박람회 시연, 오후 2시부터 시식"
          style={{ ...input, height: 'auto', resize: 'vertical' }}
          data-testid="event-content"
        />
      </label>

      <div style={field} role="group" aria-label="출고할 로봇">
        <span style={labelSt}>
          출고할 로봇 <span style={muted}>{total > 0 ? `${total}대` : '모델별 대수'}</span>
        </span>
        <div style={box}>
          {modelsQ.isLoading ? (
            <span style={hint}>모델을 불러오는 중…</span>
          ) : modelsQ.isError ? (
            <span style={{ ...hint, color: 'var(--fg-danger)' }}>
              모델 목록을 불러오지 못했습니다.
            </span>
          ) : (
            (modelsQ.data?.items ?? [])
              .filter((m) => EVENT_MODEL_CODES.includes(m.code))
              .map((m) => {
                const n = value.lines[m.id] ?? 0;
                return (
                  <div key={m.id} style={modelRow}>
                    <span style={{ fontWeight: n > 0 ? 600 : 400 }}>{m.displayName}</span>
                    <span style={stepper}>
                      <button
                        type="button"
                        aria-label={`${m.displayName} 한 대 빼기`}
                        onClick={() => setQty(m.id, n - 1)}
                        disabled={n === 0}
                        style={stepBtn}
                        className="kuun-press"
                      >
                        −
                      </button>
                      <span style={qty} data-testid={`event-qty-${m.id}`}>
                        {n}
                      </span>
                      <button
                        type="button"
                        aria-label={`${m.displayName} 한 대 더하기`}
                        onClick={() => setQty(m.id, n + 1)}
                        disabled={n >= MAX_PER_MODEL}
                        style={stepBtn}
                        className="kuun-press"
                        data-testid={`event-add-${m.id}`}
                      >
                        +
                      </button>
                    </span>
                  </div>
                );
              })
          )}
        </div>
        <span style={hint}>어느 로봇이 나갈지는 제조팀이 로봇 배정 화면에서 미리 배정합니다.</span>
      </div>

      <div style={field} role="group" aria-label="준비물">
        <span style={{ ...labelSt, display: 'flex', alignItems: 'baseline', gap: 8 }}>
          준비물{' '}
          <span style={muted}>
            {value.checklist.length > 0 ? `${value.checklist.length}가지` : '챙길 것을 고르세요'}
          </span>
          <button
            type="button"
            onClick={() => set({ checklist: allPicked ? [] : [...EVENT_PREP_ITEMS] })}
            style={linkBtn}
            data-testid="event-check-all"
          >
            {allPicked ? '모두 해제' : '모두 고르기'}
          </button>
        </span>
        <div style={checkGrid}>
          {EVENT_PREP_ITEMS.map((item) => (
            <label key={item} style={checkRow}>
              <input
                type="checkbox"
                checked={value.checklist.includes(item)}
                onChange={(e) =>
                  set({
                    checklist: e.target.checked
                      ? EVENT_PREP_ITEMS.filter((x) => x === item || value.checklist.includes(x))
                      : value.checklist.filter((x) => x !== item),
                  })
                }
                data-testid={`event-check-${item}`}
              />
              {item}
            </label>
          ))}
        </div>
        <span style={hint}>고른 준비물은 구글 캘린더 일정 메모에 적힙니다.</span>
      </div>

      <label style={field}>
        <span style={labelSt}>
          비고 <span style={muted}>(선택)</span>
        </span>
        <textarea
          value={value.note}
          onChange={(e) => set({ note: e.target.value })}
          rows={2}
          maxLength={2000}
          placeholder="예) 반입은 B홀 3번 게이트, 담당자 연락처"
          style={{ ...input, height: 'auto', resize: 'vertical' }}
          data-testid="event-note"
        />
      </label>
    </>
  );
}

const field: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelSt: CSSProperties = { fontSize: 13, fontWeight: 600 };
const muted: CSSProperties = { fontWeight: 400, color: 'var(--fg-muted)' };
const input: CSSProperties = {
  width: '100%',
  height: 36,
  padding: '6px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  boxSizing: 'border-box',
};
const hint: CSSProperties = { fontSize: 12, color: 'var(--fg-muted)' };
const warn: CSSProperties = { fontSize: 12, color: 'var(--fg-danger)', marginTop: -8 };
const warnInline: CSSProperties = { fontSize: 12, color: 'var(--fg-danger)' };
const mapBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  cursor: 'pointer',
  boxSizing: 'border-box',
};
const mapBtnOff: CSSProperties = { color: 'var(--fg-muted)', cursor: 'not-allowed', opacity: 0.6 };
// 네이버 지도 표지 — MapUrlIcon 의 초록 N 과 같은 색.
const naverMark: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  borderRadius: 4,
  background: '#03c75a',
  color: '#fff',
  fontSize: 11,
  fontWeight: 800,
  lineHeight: 1,
};
const box: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: '4px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
};
const modelRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 36,
  fontSize: 13,
};
const stepper: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4 };
const stepBtn: CSSProperties = {
  width: 28,
  height: 28,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 15,
  lineHeight: 1,
  cursor: 'pointer',
};
const qty: CSSProperties = {
  minWidth: 28,
  textAlign: 'center',
  fontSize: 13,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
};
const checkGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: '6px 12px',
  padding: '8px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
};
const checkRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  cursor: 'pointer',
};
const linkBtn: CSSProperties = {
  marginLeft: 'auto',
  padding: 0,
  border: 'none',
  background: 'none',
  color: 'var(--fg-brand)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
